import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import { downloadBlob } from '../../lib/download'

interface AuditEntry {
  id: string
  action: string
  entityType?: string
  entityId?: string
  reason?: string
  ipAddress?: string
  createdAt: string
  user?: { id: string; name: string; role: string }
  newValue?: unknown
  oldValue?: unknown
  rentalNumber?: string | null
  bikeCodes?: string[]
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  'user.login':              { label: 'Вход в систему',        color: 'text-gray-500' },
  'shift.opened':            { label: 'Смена открыта',          color: 'text-green-600' },
  'shift.closed':            { label: 'Смена закрыта',          color: 'text-gray-600' },
  'rental.opened':           { label: 'Аренда открыта',         color: 'text-blue-600' },
  'rental.closed':           { label: 'Аренда закрыта',         color: 'text-green-700' },
  'rental.created_manual':   { label: 'Аренда внесена вручную', color: 'text-amber-600' },
  'rental.price_changed':    { label: 'Цена аренды изменена',   color: 'text-purple-600' },
  'bike.status_changed':     { label: 'Статус велосипеда',      color: 'text-orange-600' },
  'bike.moved':              { label: 'Велосипед перемещён',    color: 'text-orange-700' },
  'bike.updated':            { label: 'Велосипед изменён',      color: 'text-gray-600' },
  'bike.created':            { label: 'Велосипед добавлен',     color: 'text-teal-600' },
  'payment.created':         { label: 'Платёж принят',          color: 'text-green-600' },
  'payment.updated':         { label: 'Платёж изменён',         color: 'text-purple-700' },
  'stock.receipt':           { label: 'Склад: приход',          color: 'text-teal-600' },
  'stock.transfer':          { label: 'Склад: перемещение',     color: 'text-blue-600' },
  'stock.writeoff':          { label: 'Склад: списание',        color: 'text-orange-600' },
  'point.created':           { label: 'Точка добавлена',        color: 'text-teal-600' },
  'tariff.updated':          { label: 'Тариф изменён',          color: 'text-purple-600' },
  'user.create_manager':     { label: 'Менеджер создан',        color: 'text-blue-500' },
  'user.deactivate_manager': { label: 'Менеджер отключён',      color: 'text-red-500' },
  'ocr.verify':              { label: 'Проверка документа',     color: 'text-indigo-500' },
}

export default function AuditPage() {
  const [page, setPage] = useState(0)
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [exporting, setExporting] = useState(false)
  const limit = 30

  useEffect(() => {
    const t = window.setTimeout(() => { setDebouncedSearch(search.trim()); setPage(0) }, 350)
    return () => window.clearTimeout(t)
  }, [search])

  const { data, isLoading } = useQuery({
    queryKey: ['audit', page, from, to, debouncedSearch],
    queryFn: () =>
      api.get('/audit', { params: {
        limit, offset: page * limit,
        from: new Date(from).toISOString(), to: new Date(to + 'T23:59:59').toISOString(),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      } }).then((r) => r.data),
  })

  const entries: AuditEntry[] = data?.logs ?? []
  const total: number = data?.total ?? 0

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await api.get('/audit/csv', {
        responseType: 'blob',
        params: { from: new Date(from).toISOString(), to: new Date(to + 'T23:59:59').toISOString() },
      })
      await downloadBlob(res.data as Blob, `audit-${from}-${to}.csv`)
    } catch { alert('Ошибка экспорта') }
    finally { setExporting(false) }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Аудит-лог</h2>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="text-sm border border-gray-300 text-gray-600 px-3 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-60"
        >
          {exporting ? 'Экспорт...' : '📥 CSV'}
        </button>
      </div>

      {/* Date range filter */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">С</label>
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0) }}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">По</label>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0) }}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Search by rental number / bike code */}
      <div className="relative mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по № аренды или коду велосипеда (напр. 256 или 489)"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm pl-9 bg-gray-50 focus:outline-none focus:border-brand-400"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-4">Всего: {total}</p>

      {isLoading && <p className="text-gray-400 text-sm py-4 text-center">Загрузка...</p>}

      <div className="space-y-1">
        {entries.map((entry) => {
          const meta = ACTION_LABELS[entry.action]
          return (
            <div key={entry.id} className="bg-white border border-gray-100 rounded-xl px-4 py-3 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${meta?.color ?? 'text-gray-700'}`}>
                    {meta?.label ?? entry.action}
                  </span>
                  {entry.user && (
                    <span className="text-xs text-gray-400">
                      · {entry.user.name}
                    </span>
                  )}
                  {entry.reason && (
                    <span className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-700 px-2 py-0.5 rounded-full">
                      причина: {entry.reason}
                    </span>
                  )}
                </div>
                {(entry.rentalNumber || (entry.bikeCodes && entry.bikeCodes.length > 0)) ? (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {entry.rentalNumber && (
                      <span className="text-xs bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        аренда {entry.rentalNumber}
                      </span>
                    )}
                    {entry.bikeCodes?.map((code) => (
                      <span key={code} className="text-xs bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5 rounded-full font-medium font-mono">
                        велик #{code}
                      </span>
                    ))}
                  </div>
                ) : entry.entityType ? (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {entry.entityType}{entry.entityId ? ` · ${entry.entityId.slice(0, 8)}…` : ''}
                  </p>
                ) : null}
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {new Date(entry.createdAt).toLocaleString('ru', {
                  day: '2-digit', month: '2-digit',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
          )
        })}
      </div>

      {total > limit && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-2 rounded-xl border border-gray-300 text-sm disabled:opacity-40"
          >
            ← Назад
          </button>
          <span className="text-sm text-gray-500">
            {page + 1} / {Math.ceil(total / limit)}
          </span>
          <button
            disabled={(page + 1) * limit >= total}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 rounded-xl border border-gray-300 text-sm disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  )
}
